import { useMutation, useQueryClient } from "@tanstack/react-query"
import { postRequest, patchRequest } from "../helpers/fetchHandler"
import { setAuthTokens, clearAuthTokens } from "../storage/authStorage"
import { clearPreferences } from "../storage/preferencesStorage"
import { useAuth } from "../contexts/AuthContext"
import type { AuthUser } from "../constants/types"

type LoginData = { email: string; password: string }
type RegisterData = { email: string; password: string; firstName: string; lastName: string; language: string }
type ResetEmailData = { email: string; language: string }
type ResetPasswordData = { password: string; token: string; language: string }
type UpdateProfileData = { firstName: string; lastName: string; email: string; language: string }
type ChangePasswordData = { currentPassword: string; newPassword: string }

type LoginResponse = {
  accessToken: string
  accessTokenExpiresAt: string
  refreshToken: string
  refreshTokenExpiresAt: string
  userId: string
  email: string
  firstName: string
  lastName: string
  language: string
  isEmailVerified: boolean
}

export function useLogin() {
  const { setUser } = useAuth()

  return useMutation({
    mutationFn: async (data: LoginData) => {
      const response = (await postRequest({ url: "/api/v1/auth/login", data })) as LoginResponse
      setAuthTokens(response)
      const user: AuthUser = {
        id: response.userId,
        email: response.email,
        firstName: response.firstName,
        lastName: response.lastName,
        language: response.language,
        isEmailVerified: response.isEmailVerified
      }
      setUser(user)
      return response
    }
  })
}

export function useRegister() {
  return useMutation({
    mutationFn: (data: RegisterData) =>
      postRequest({ url: "/api/v1/auth/register/", data }) as Promise<unknown>
  })
}

export function useLogout() {
  const { clearUser } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      try {
        await postRequest({ url: "/api/v1/auth/logout", auth: true, skipAuthHandling: true })
      } catch {
        // Ignore errors - we're logging out anyway
      }
      clearAuthTokens()
      clearPreferences()
      clearUser()
      queryClient.clear()
    }
  })
}

export function useUpdateProfile() {
  const { user, setUser } = useAuth()

  return useMutation({
    mutationFn: async (data: UpdateProfileData) => {
      const currentEmail = user?.email
      const response = (await patchRequest({
        url: "/api/v1/profile/user",
        data,
        auth: true
      })) as AuthUser
      const emailChanged = currentEmail !== response.email
      setUser(response)
      return { ...response, emailChanged }
    }
  })
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (data: ChangePasswordData) =>
      patchRequest({ url: "/api/v1/profile/password", data, auth: true }) as Promise<unknown>
  })
}

export function useRequestResetEmail() {
  return useMutation({
    mutationFn: (data: ResetEmailData) =>
      postRequest({ url: "/api/v1/auth/request-reset-email", data }) as Promise<unknown>
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (data: ResetPasswordData) =>
      postRequest({ url: "/api/v1/auth/reset-password", data }) as Promise<unknown>
  })
}
